/**
 * Mission data mapper - transforms API response to UI model
 */

import { getDictValue } from '@/lib/utils/dictMapper';

export interface MissionImage {
  src: string;
  alt: string;
  placeholder: string;
  description: string;
}

export interface MissionCTA {
  text: string;
  variant: string;
  size: string;
  style: {
    backgroundColor: string;
    color: string;
    borderRadius: string;
  };
}

export interface MissionLayout {
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  badgeColor: string;
  gridCols: string;
  gap: number;
  padding: number;
  borderRadius: string;
}

export interface MissionTypography {
  title: {
    fontFamily: string;
    fontSize: string;
    fontWeight: string;
    lineHeight: string;
    color: string;
  };
  subtitle: {
    fontFamily: string;
    fontSize: string;
    fontWeight: string;
    lineHeight: string;
    color: string;
  };
  badge: {
    fontSize: string;
    fontWeight: string;
    textTransform: string;
    letterSpacing: string;
    color: string;
  };
}

export interface MissionModel {
  title: string;
  subtitle: string;
  badge: string;
  image: MissionImage;
  cta: MissionCTA;
  layout: MissionLayout;
  typography: MissionTypography;
}

export interface MissionApiResponse {
  title: string;
  subtitle: string;
  badge: string;
  image: MissionImage;
  cta: MissionCTA;
  layout: MissionLayout;
  typography: MissionTypography;
}

/**
 * Maps API response to Mission UI model
 */
export function mapMissionFromApi(apiData: MissionApiResponse): MissionModel {
  return {
    title: apiData.title,
    subtitle: apiData.subtitle,
    badge: apiData.badge,
    image: {
      src: apiData.image.src,
      alt: apiData.image.alt,
      placeholder: apiData.image.placeholder,
      description: apiData.image.description
    },
    cta: {
      text: apiData.cta.text,
      variant: apiData.cta.variant,
      size: apiData.cta.size,
      style: {
        backgroundColor: apiData.cta.style.backgroundColor,
        color: apiData.cta.style.color,
        borderRadius: apiData.cta.style.borderRadius
      }
    },
    layout: {
      backgroundColor: apiData.layout?.backgroundColor || "#F8FAFC",
      borderColor: apiData.layout?.borderColor || "#E2E8F0",
      textColor: apiData.layout?.textColor || "#1E293B",
      badgeColor: apiData.layout?.badgeColor || "#3B82F6",
      gridCols: apiData.layout?.gridCols || "2",
      gap: apiData.layout?.gap || 8,
      padding: apiData.layout?.padding || 8,
      borderRadius: apiData.layout?.borderRadius || "12px"
    },
    typography: {
      title: {
        fontFamily: apiData.typography?.title?.fontFamily || "Albert Sans",
        fontSize: apiData.typography?.title?.fontSize || "2.5rem",
        fontWeight: apiData.typography?.title?.fontWeight || "700",
        lineHeight: apiData.typography?.title?.lineHeight || "1.2",
        color: apiData.typography?.title?.color || "#1E293B"
      },
      subtitle: {
        fontFamily: apiData.typography?.subtitle?.fontFamily || "Albert Sans",
        fontSize: apiData.typography?.subtitle?.fontSize || "1.125rem",
        fontWeight: apiData.typography?.subtitle?.fontWeight || "400",
        lineHeight: apiData.typography?.subtitle?.lineHeight || "1.6",
        color: apiData.typography?.subtitle?.color || "#64748B"
      },
      badge: {
        fontSize: apiData.typography?.badge?.fontSize || "0.875rem",
        fontWeight: apiData.typography?.badge?.fontWeight || "600",
        textTransform: apiData.typography?.badge?.textTransform || "uppercase",
        letterSpacing: apiData.typography?.badge?.letterSpacing || "0.05em",
        color: apiData.typography?.badge?.color || "#3B82F6"
      }
    }
  };
}

/**
 * Maps API response to Mission UI model with dictionary translation
 */
export function mapMissionFromApiWithDict(apiData: MissionApiResponse, dict: any): MissionModel {
  const mapped = mapMissionFromApi(apiData);
  
  // Map dictionary keys to actual values
  return {
    title: getDictValue(dict, mapped.title),
    subtitle: getDictValue(dict, mapped.subtitle),
    badge: mapped.badge,
    image: {
      src: mapped.image.src,
      alt: mapped.image.alt,
      placeholder: mapped.image.placeholder,
      description: mapped.image.description
    },
    cta: {
      text: getDictValue(dict, mapped.cta.text),
      variant: mapped.cta.variant,
      size: mapped.cta.size,
      style: mapped.cta.style
    },
    layout: mapped.layout,
    typography: mapped.typography
  };
}
